import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

import { env } from "@/lib/env";
import { ApiError } from "@/lib/http";
import { useAuth } from "./auth";

/**
 * HTTP client for the authenticated admin API.
 *
 * Separate from the public client on purpose. The public one must never send a
 * bearer token — it is used for pages a search engine crawls — and this one
 * must always send it and know how to refresh. Sharing an instance would mean
 * one interceptor deciding both, which is where "why is the public site sending
 * my admin token" comes from.
 */

export const adminHttp: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
  headers: { "Content-Type": "application/json" },

  /** Repeat the key for arrays: FastAPI reads `status[]=a` as a parameter
      literally named "status[]" and silently ignores it. */
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === "") continue;
        if (Array.isArray(value)) for (const item of value) search.append(key, String(item));
        else search.append(key, String(value));
      }
      return search.toString();
    },
  },
});

adminHttp.interceptors.request.use((config) => {
  const token = useAuth.getState().accessToken;
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  config.headers.set("X-Request-Id", crypto.randomUUID());
  return config;
});

/**
 * Refresh-on-401, with a single flight.
 *
 * Refresh tokens rotate: the server revokes the one presented and issues a new
 * pair. If five requests 401 at once and each refreshes, four of them present a
 * token that the first already burned, and the session dies. One shared promise
 * means one refresh, and everyone waits for it.
 */
let refreshing: Promise<string | null> | null = null;

async function refreshSession(): Promise<string | null> {
  const { refreshToken, setTokens, signOut } = useAuth.getState();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${env.apiBaseUrl}/admin/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { "Content-Type": "application/json" } },
    );
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    // The refresh token is spent or revoked; there is nothing to recover.
    signOut();
    return null;
  }
}

adminHttp.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(toAdminError(error));

    const status = error.response?.status ?? 0;
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

    const canRetry =
      status === 401 &&
      config &&
      !config._retried &&
      // Never loop on the refresh call itself.
      !String(config.url ?? "").includes("/auth/refresh");

    if (canRetry) {
      config._retried = true;
      refreshing ??= refreshSession().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) return adminHttp(config);
    }

    return Promise.reject(toAdminError(error));
  },
);

function toAdminError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;

    // FastAPI validation errors arrive as a list of {loc, msg}.
    if (Array.isArray(detail)) {
      const fields: Record<string, string> = {};
      for (const item of detail as { loc?: unknown[]; msg?: string }[]) {
        const field = (item.loc ?? []).filter((p) => p !== "body").join(".");
        if (field) fields[field] = item.msg ?? "Invalid";
      }
      return new ApiError("Please check the highlighted fields.", status, "validation", fields);
    }

    if (typeof detail === "string") return new ApiError(detail, status, "http");
    if (status === 0) {
      return new ApiError("Could not reach the server.", 0, "network");
    }
    return new ApiError(error.message, status, "http");
  }

  return new ApiError("Something went wrong.", 0, "unknown");
}

/* -------------------------------------------------------------------------- */

const get = async <T,>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  (await adminHttp.get<T>(url, config)).data;
const post = async <T,>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> =>
  (await adminHttp.post<T>(url, body, config)).data;
const patch = async <T,>(url: string, body?: unknown): Promise<T> =>
  (await adminHttp.patch<T>(url, body)).data;
const put = async <T,>(url: string, body?: unknown): Promise<T> =>
  (await adminHttp.put<T>(url, body)).data;
const del = async (url: string): Promise<void> => {
  await adminHttp.delete(url);
};

/* ------------------------------------------------------------------- types */

export interface ContentField {
  name: string;
  control:
    | "text"
    | "textarea"
    | "richtext"
    | "slug"
    | "number"
    | "boolean"
    | "date"
    | "datetime"
    | "json"
    | "blocks"
    | "image"
    | "id";
  required: boolean;
  maxLength: number | null;
  reference: string | null;
}

export interface ContentType {
  entityType: string;
  label: string;
  fields: ContentField[];
}

export interface ContentRecord {
  entity_type: string;
  id: string;
  slug: string | null;
  title: string | null;
  state: "draft" | "in_review" | "scheduled" | "published" | "archived";
  version: number;
  published_at: string | null;
  scheduled_for: string | null;
  updated_at: string | null;
  data: Record<string, unknown>;
}

export interface Revision {
  id: string;
  version: number;
  state: string;
  change_summary: string | null;
  author_label: string;
  is_current: boolean;
  created_at: string;
}

export interface MediaAsset {
  id: string;
  filename: string;
  original_filename: string;
  kind: string;
  mime_type: string;
  size_bytes: number;
  public_url: string | null;
  alt_text: string | null;
  is_protected: boolean;
  folder: string | null;
  created_at: string;
}

export interface Setting {
  id: string;
  key: string;
  value: unknown;
  group: string;
  label: string;
  description: string | null;
  is_public: boolean;
}

/* ---------------------------------------------------------------- endpoints */

export const adminApi = {
  auth: {
    login: (email: string, password: string) =>
      post<{ access_token: string; refresh_token: string }>("/admin/auth/login", {
        email,
        password,
      }),
    me: () =>
      get<{ user: AdminMe; permissions: string[] }>("/admin/auth/me"),
    logout: () => post<void>("/admin/auth/logout"),
  },

  contentTypes: {
    list: () => get<ContentType[]>("/admin/content-types"),
    one: (entityType: string) => get<ContentType>(`/admin/content-types/${entityType}`),
  },

  content: {
    list: (entityType: string, params: { state?: string; q?: string; page?: number } = {}) =>
      get<ContentRecord[]>(`/admin/content/${entityType}`, { params }),
    one: (entityType: string, id: string) =>
      get<ContentRecord>(`/admin/content/${entityType}/${id}`),
    create: (entityType: string, data: Record<string, unknown>, publish = false) =>
      post<ContentRecord>(`/admin/content/${entityType}`, { data, publish }),
    update: (entityType: string, id: string, data: Record<string, unknown>, summary?: string) =>
      patch<ContentRecord>(`/admin/content/${entityType}/${id}`, {
        data,
        change_summary: summary,
      }),
    publish: (entityType: string, id: string) =>
      post<ContentRecord>(`/admin/content/${entityType}/${id}/publish`),
    unpublish: (entityType: string, id: string) =>
      post<ContentRecord>(`/admin/content/${entityType}/${id}/unpublish`),
    schedule: (entityType: string, id: string, when: string) =>
      post<ContentRecord>(`/admin/content/${entityType}/${id}/schedule`, { scheduled_for: when }),
    remove: (entityType: string, id: string) => del(`/admin/content/${entityType}/${id}`),
    revisions: (entityType: string, id: string) =>
      get<Revision[]>(`/admin/content/${entityType}/${id}/revisions`),
    rollback: (revisionId: string) =>
      post<ContentRecord>(`/admin/revisions/${revisionId}/rollback`),
  },

  media: {
    list: (params: { kind?: string; folder?: string; page?: number } = {}) =>
      get<MediaAsset[]>("/admin/media", { params }),
    upload: (file: File, options: { folder?: string; alt?: string; protected?: boolean } = {}) => {
      const form = new FormData();
      form.append("file", file);
      if (options.folder) form.append("folder", options.folder);
      if (options.alt) form.append("alt_text", options.alt);
      form.append("is_protected", String(Boolean(options.protected)));
      // Let the browser set the multipart boundary; a hand-set Content-Type
      // omits it and the server cannot parse the body.
      return post<MediaAsset>("/admin/media", form, { headers: { "Content-Type": undefined } });
    },
    update: (id: string, patchBody: Partial<MediaAsset>) =>
      patch<MediaAsset>(`/admin/media/${id}`, patchBody),
    remove: (id: string) => del(`/admin/media/${id}`),
  },

  settings: {
    list: (group?: string) => get<Setting[]>("/admin/settings", { params: { group } }),
    save: (key: string, value: unknown) => put<Setting>(`/admin/settings/${key}`, { value }),
  },

  dashboard: () => get<AdminDashboard>("/admin/dashboard"),
};

export interface AdminMe {
  id: string;
  email: string;
  full_name: string;
  is_superuser: boolean;
}

export interface AdminDashboard {
  crm: Record<string, number>;
  finance: Record<string, number>;
  marketplace: Record<string, number>;
  queues: Record<string, number>;
  content: { drafts: Record<string, number> };
  automation: Record<string, number>;
}
